import { ref, type Ref } from 'vue';
import {
  listMatrixCodeRevisions,
  type MatrixCodeRevision,
  type MatrixCodeRevisionsPage,
} from '@/entities/Matrix';

export const DEFAULT_MATRIX_REVISIONS_PER_PAGE = 10;

export function useMatrixRevisionsTab(
  prefix: Ref<string>,
  initialPage: MatrixCodeRevisionsPage | null = null,
  matrixId: number | null = null
) {
  const page = ref(1);
  const itemsPerPage = ref(DEFAULT_MATRIX_REVISIONS_PER_PAGE);
  const rows = ref<MatrixCodeRevision[]>([]);
  const total = ref(0);
  const restricted = ref(false);
  const loading = ref(false);
  const error = ref(false);
  let latestRequest = 0;

  const publish = (result: MatrixCodeRevisionsPage) => {
    rows.value = result.data;
    total.value = result.metadata.total;
    restricted.value = result.matricesRestricted;
  };

  async function fetch(): Promise<void> {
    const request = ++latestRequest;

    loading.value = true;
    error.value = false;

    try {
      const result = await listMatrixCodeRevisions(prefix.value, {
        page: page.value,
        itemsPerPage: itemsPerPage.value,
        ...(matrixId !== null && { matrixId }),
      });
      if (request !== latestRequest) return;
      publish(result);
    } catch {
      if (request !== latestRequest) return;
      rows.value = [];
      error.value = true;
    } finally {
      if (request === latestRequest) {
        loading.value = false;
      }
    }
  }

  function goToPage(next: number): Promise<void> {
    page.value = next;
    return fetch();
  }

  function changeItemsPerPage(quantity: number): Promise<void> {
    itemsPerPage.value = quantity;
    page.value = 1;
    return fetch();
  }

  if (initialPage) {
    page.value = initialPage.metadata.page;
    itemsPerPage.value = initialPage.metadata.itemsPerPage ?? DEFAULT_MATRIX_REVISIONS_PER_PAGE;
    publish(initialPage);
  } else {
    fetch();
  }

  return {
    page,
    itemsPerPage,
    rows,
    total,
    restricted,
    loading,
    error,
    fetch,
    retry: fetch,
    goToPage,
    changeItemsPerPage,
  };
}
